import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, RotateCcw, FileText, Flame, Info } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const fmtDeleted = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
};

export default function Trash() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices/trash");
      setRows(data);
    } catch {
      toast.error("Failed to load Recycle Bin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = !allSelected && visibleIds.some((id) => selected.has(id));

  const toggleAll = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => n.delete(id));
      else visibleIds.forEach((id) => n.add(id));
      return n;
    });
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const restoreOne = async (id) => {
    try {
      await api.post(`/invoices/${id}/restore`);
      toast.success("Restored to Invoice History");
      setSelected((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      load();
    } catch {
      toast.error("Failed to restore");
    }
  };

  const deleteOnePermanent = async (id) => {
    if (!window.confirm("Permanently delete this invoice? This CANNOT be undone.")) return;
    try {
      await api.delete(`/invoices/${id}/permanent`);
      toast.success("Permanently deleted");
      setSelected((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const restoreBulk = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/trash/restore-bulk", { ids });
      toast.success(`Restored ${data.restored} invoice${data.restored === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Bulk restore failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteBulkPermanent = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (
      !window.confirm(
        `Permanently delete ${ids.length} invoice${ids.length === 1 ? "" : "s"}? This CANNOT be undone.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/trash/delete-bulk", { ids });
      toast.success(`Permanently deleted ${data.deleted} invoice${data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Bulk delete failed");
    } finally {
      setBusy(false);
    }
  };

  const emptyBin = async () => {
    if (!rows.length) return;
    if (
      !window.confirm(
        `Empty the Recycle Bin? ${rows.length} invoice${rows.length === 1 ? "" : "s"} will be permanently deleted.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/trash/empty");
      toast.success(`Emptied — ${data.deleted} invoice${data.deleted === 1 ? "" : "s"} permanently deleted`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Failed to empty bin");
    } finally {
      setBusy(false);
    }
  };

  const selectedCount = selected.size;

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            Recycle Bin
          </div>
          <h1 className="text-4xl font-bold tracking-tight" data-testid="trash-heading">
            Deleted Invoices
          </h1>
          <p className="text-sm text-zinc-500 mt-2 flex items-center gap-1.5">
            <Info size={13} className="shrink-0" />
            Invoices stay here for <b>30 days</b>, then are permanently deleted automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={emptyBin}
            disabled={busy || rows.length === 0}
            data-testid="trash-empty-btn"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            <Flame size={14} className="mr-1" />
            Empty Bin
          </Button>
        </div>
      </div>

      {selectedCount > 0 && (
        <div
          className="flex items-center justify-between mb-3 px-4 py-2 rounded-md bg-zinc-100 border border-zinc-200"
          data-testid="trash-action-bar"
        >
          <div className="text-sm text-zinc-700">
            <b data-testid="trash-selected-count">{selectedCount}</b> selected
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs"
              onClick={() => setSelected(new Set())}
              data-testid="trash-clear-selection"
            >
              Clear
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={restoreBulk}
              disabled={busy}
              data-testid="trash-bulk-restore"
            >
              <RotateCcw size={13} className="mr-1" />
              Restore {selectedCount}
            </Button>
            <Button
              size="sm"
              className="h-8 bg-red-600 hover:bg-red-700 text-xs"
              onClick={deleteBulkPermanent}
              disabled={busy}
              data-testid="trash-bulk-delete"
            >
              <Trash2 size={13} className="mr-1" />
              Delete Forever
            </Button>
          </div>
        </div>
      )}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500">
            <tr className="text-left">
              <th className="py-3 pl-4 pr-2 w-8">
                <Checkbox
                  checked={
                    allSelected ? true : someSelected ? "indeterminate" : false
                  }
                  onCheckedChange={toggleAll}
                  disabled={rows.length === 0}
                  aria-label="Select all"
                  data-testid="trash-select-all"
                />
              </th>
              <th className="py-3 px-4 font-medium">Invoice #</th>
              <th className="py-3 px-4 font-medium">Buyer</th>
              <th className="py-3 px-4 font-medium">Deleted</th>
              <th className="py-3 px-4 font-medium text-amber-700">Auto-delete in</th>
              <th className="py-3 px-4 font-medium text-right">Amount (₹)</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="py-12 text-center text-zinc-400 text-sm">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-12 text-center text-zinc-400" data-testid="trash-empty">
                  <FileText size={22} className="inline mb-2" />
                  <div className="text-sm">Recycle Bin is empty</div>
                </td>
              </tr>
            ) : (
              rows.map((inv) => {
                const isSelected = selected.has(inv.id);
                const days = Number(inv.days_until_purge ?? 30);
                const warn = days <= 7;
                return (
                  <tr
                    key={inv.id}
                    className={`border-t border-zinc-100 ${isSelected ? "bg-zinc-50" : ""}`}
                    data-testid={`trash-row-${inv.invoice_number}`}
                  >
                    <td className="py-3 pl-4 pr-2">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleRow(inv.id)}
                        aria-label={`Select ${inv.invoice_number}`}
                        data-testid={`trash-select-${inv.invoice_number}`}
                      />
                    </td>
                    <td className="py-3 px-4 mono font-medium text-zinc-500">
                      {inv.invoice_number}
                    </td>
                    <td className="py-3 px-4 text-zinc-500 max-w-[280px] truncate">
                      {inv.buyer?.name}
                    </td>
                    <td className="py-3 px-4 text-zinc-500 whitespace-nowrap text-xs">
                      {fmtDeleted(inv.deleted_at)}
                    </td>
                    <td
                      className={`py-3 px-4 whitespace-nowrap text-xs ${
                        warn ? "text-red-600 font-medium" : "text-zinc-500"
                      }`}
                    >
                      {days} day{days === 1 ? "" : "s"}
                    </td>
                    <td className="py-3 px-4 mono tabular text-right text-zinc-500">
                      {inv.grand_total?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 flex items-center gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-xs"
                        onClick={() => restoreOne(inv.id)}
                        data-testid={`trash-restore-${inv.invoice_number}`}
                        title="Restore to Invoice History"
                      >
                        <RotateCcw size={13} className="mr-1" />
                        Restore
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-zinc-400 hover:text-red-600"
                        onClick={() => deleteOnePermanent(inv.id)}
                        data-testid={`trash-delete-${inv.invoice_number}`}
                        title="Delete forever"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
